import React, { useEffect, useState } from 'react';

import { FormDialog } from '@components/admin/FormDialog';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { FormAlert } from '@components/Forms/FormAlert';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { SpinnerModal } from '@components/SpinnerModal';
import { agencyStrings } from '@data/agency';
import { usePostAgency } from '@hooks/mutation/dashboard/usePostAgency';
import { useFcAgency } from '@hooks/queries/dashboard/useFcAgency';
import { agencyForm, AgencyForm, agencyInitialValues, FcAgencyGetQuery } from '@models/Agency';
import { makeMuiStyles, useIsMobile } from '@styles';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { Formik, Form, useFormikContext } from 'formik';

const strings = createStrings({
  ja: {
    addItemsTitle: '代理店を追加する',
    inputFromKikanSystem: '基幹システムから導入する',
  },
});

const useStyles = makeMuiStyles((_theme, _common) => ({
  body: {
    overflow: 'auto',
    maxWidth: '100%',
    width: '60rem',
  },
}));

interface GetFcAgencyContainerProps {
  children: React.ReactNode;
}

const GetFcAgencyContainer: React.FC<GetFcAgencyContainerProps> = (props) => {
  const { children } = props;
  const [query, setQuery] = useState<FcAgencyGetQuery>({ agent_code: '', agent_english: '' });
  const enableFcAgency = Boolean(query.agent_code) && Boolean(query.agent_english);
  const fcAgency = useFcAgency(query, {
    enabled: enableFcAgency,
  });
  const { values, setValues, setTouched } = useFormikContext<AgencyForm>();

  useEffect(() => {
    if (fcAgency.isSuccess) {
      setValues({ ...fcAgency.data, name_en_short: values.name_en_short });
    }
  }, [fcAgency.isSuccess, fcAgency.data, query]);

  return (
    <>
      <SpinnerModal open={fcAgency.isLoading && enableFcAgency} />
      <Alert open={fcAgency.isError} severity={'error'}>
        {(fcAgency.error as Error)?.message ?? formStrings.unknownError}
      </Alert>
      {children}
      <Button
        color="primary"
        size="small"
        shape="round"
        text={strings.inputFromKikanSystem}
        style={{ minWidth: 80 }}
        type="button"
        onClick={() => {
          setTouched({ name_en: true, fcp_no: true }, true);
          setQuery({ agent_code: values.fcp_no, agent_english: values.name_en });
        }}
      />
    </>
  );
};

interface AgencyAddItemDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AgencyAddItemDialog: React.FC<AgencyAddItemDialogProps> = (props) => {
  const { open, onClose } = props;
  const classes = useStyles();
  const isMobile = useIsMobile();
  const postAgency = usePostAgency();

  return (
    <FormDialog
      dialogProps={{ maxWidth: false }}
      title={strings.addItemsTitle}
      alignTitle="center"
      open={open}
      onClose={onClose}
      classes={{ body: classes.body }}
    >
      <Formik<AgencyForm>
        initialValues={agencyInitialValues}
        onSubmit={async (values) => {
          postAgency.mutate(values);
        }}
        validate={createValidate(agencyForm)}
      >
        <Form>
          <FormAlert mutation={postAgency} />
          <SubmittingOverlay />

          <GetFcAgencyContainer>
            <ManagementTextInput
              name={'name_en'}
              label={agencyStrings.englishName}
              labelDestructive={isMobile}
              required
            />
            <ManagementTextInput
              name={'fcp_no'}
              label={agencyStrings.fcpNo}
              labelDestructive={isMobile}
              required
            />
          </GetFcAgencyContainer>

          <ManagementTextInput
            name={'name_en_short'}
            label={agencyStrings.englishNameShort}
            labelDestructive={isMobile}
            required
          />

          <ManagementTextInput
            name={'name'}
            label={agencyStrings.japaneseName}
            labelDestructive={isMobile}
          />
          <ManagementTextInput
            name={'name_short'}
            label={agencyStrings.japaneseNameShort}
            labelDestructive={isMobile}
          />
          <ManagementTextInput
            name={'address1'}
            label={agencyStrings.address1}
            labelDestructive={isMobile}
          />
          <ManagementTextInput
            name={'address2'}
            label={agencyStrings.address2}
            labelDestructive={isMobile}
          />
          <ManagementTextInput
            name={'phone'}
            label={agencyStrings.phone}
            labelDestructive={isMobile}
          />
          <ManagementTextInput name={'fax'} label={agencyStrings.fax} labelDestructive={isMobile} />
        </Form>
      </Formik>
    </FormDialog>
  );
};
